import './TopHeader.scss';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Autocomplete from '@mui/material/Autocomplete';
import { AccountButton } from './buttons/AccountButton';
import { OpenStandaloneButton } from './buttons/OpenStandaloneButton';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import LuggageOutlinedIcon from '@mui/icons-material/LuggageOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { MyAvatar } from '../common/MyAvatar';
import { FancyIconButton } from '../common/buttons';
import { setSideNavOpen } from '../../store/Actions';
import { buildSectionPath, useAppNavigate } from '../../utils/Routing';
import SecondMemoriesIcon from '../../assets/SecondMemoriesIcon.svg';

const secondmemoriesSearchOptions = [
  { label: 'Sarah Cohen', detail: 'Account · New York, NY' },
  { label: 'Daniel Levy', detail: 'Account · Tel Aviv, Israel' },
  { label: 'Maya Patel', detail: 'Account · San Francisco, CA' },
  { label: 'Noah Williams', detail: 'Account · Austin, TX' },
  { label: 'Emma Rodriguez', detail: 'Account · Mexico City, Mexico' },
  { label: 'Liam Chen', detail: 'Account · Vancouver, Canada' },
  { label: 'Ava Thompson', detail: 'Account · London, England' },
  { label: 'Ethan Brooks', detail: 'Account · Miami, FL' },
  { label: 'Discover People', detail: 'Find accounts to follow' },
  { label: 'Invite Friends', detail: 'Share Second Memories with someone new' },
  { label: 'Public profiles', detail: 'Explore featured travel profiles' },
  { label: 'Nearby Memories', detail: 'See public activity around you' },
  { label: 'Trending Trips', detail: 'Popular public itineraries' },
  { label: 'Shared With Me', detail: 'Events and trips others sent you' },
  { label: 'Messages', detail: 'Open conversations' },
  { label: 'Notifications', detail: 'Account updates and activity' },
  { label: 'Settings', detail: 'Account preferences' },
];

export function TopHeader() {
  const [secondmemoriesSearchValue, setSecondmemoriesSearchValue] = useState(null);
  const embed = useSelector(state => state.embed);
  const sideNavOpen = useSelector(state => state.sideNavOpen);
  const appWidth = useSelector(state => state.appWidth);
  const viewingUserName = useSelector(state => state.viewingUserName);
  const viewingUserAccount = useSelector(state => state.viewingUserAccount);
  const appView = useSelector(state => state.appView);
  const dialogOpen = useSelector(state => state.dialogOpen);
  const dispatch = useDispatch();
  const navigateTo = useAppNavigate();

  // Each nav button navigates to /[username]/[section]. The URL→Redux effect
  // in App.js picks up the path change and updates state.appView and any
  // open View dialog. useAppNavigate also clears filter Redux + filter
  // search params on section changes.
  const username = viewingUserAccount?.username;
  const goToView = (targetView) => {
    if(!username) return;
    navigateTo(buildSectionPath(username, targetView));
  };

  const navItems = [
    {label: 'About', icon: <InfoOutlinedIcon/>, isSelected: appView === 'About' && !dialogOpen, onClick: () => goToView('About')},
    {label: 'Map', icon: <MapOutlinedIcon/>, isSelected: appView === 'Map' && !dialogOpen, onClick: () => goToView('Map')},
    {label: 'Timeline', icon: <TimelineOutlinedIcon/>, isSelected: appView === 'Timeline' && !dialogOpen, onClick: () => goToView('Timeline')},
    {label: 'Friends', icon: <PeopleAltOutlinedIcon/>, isSelected: appView === 'Friends' && !dialogOpen, onClick: () => goToView('Friends')},
    {label: 'Trips', icon: <LuggageOutlinedIcon/>, isSelected: appView === 'Trips' && !dialogOpen, onClick: () => goToView('Trips')},
    {label: 'Categories', icon: <CategoryOutlinedIcon/>, isSelected: appView === 'Categories' && !dialogOpen, onClick: () => goToView('Categories')},
    {label: 'Tags', icon: <LocalOfferOutlinedIcon/>, isSelected: appView === 'Tags' && !dialogOpen, onClick: () => goToView('Tags')},
  ];

  const handleNavItemClick = (item) => {
    item.onClick();
    if(appWidth <= 560){
      dispatch(setSideNavOpen(false));
    }
  };

  useEffect(() => {
    document.body.classList.toggle('top-header-side-nav-open', sideNavOpen && !embed);

    return () => {
      document.body.classList.remove('top-header-side-nav-open');
    };
  }, [embed, sideNavOpen]);

  return (
    <>
      <div className="top-header">
        {/* Left — brand block */}
        <div className="top-header__brand">
          {!embed && (
            <>
              <button
                className="top-header__logo-button"
                type="button"
                aria-label={sideNavOpen ? 'Close navigation' : 'Open navigation'}
                aria-expanded={sideNavOpen}
                onClick={() => dispatch(setSideNavOpen(!sideNavOpen))}
              >
                <img className="top-header__app-icon" src={SecondMemoriesIcon} alt=""/>
              </button>
              <Autocomplete
                className="top-header-secondmemories-search"
                options={secondmemoriesSearchOptions}
                value={secondmemoriesSearchValue}
                onChange={(event, newValue) => setSecondmemoriesSearchValue(newValue)}
                getOptionLabel={(option) => option?.label || ''}
                popupIcon={null}
                clearOnBlur={false}
                slotProps={{
                  popper: { className: 'top-header-secondmemories-search__popper' },
                }}
                renderOption={(optionProps, option) => (
                  <li {...optionProps} className={`${optionProps.className || ''} top-header-secondmemories-search__option`}>
                    <span className="top-header-secondmemories-search__option-title">{option.label}</span>
                    <span className="top-header-secondmemories-search__option-detail">{option.detail}</span>
                  </li>
                )}
                renderInput={(params) => {
                  const { InputProps, inputProps } = params;
                  const { className: inputClassName = '', ...restInputProps } = inputProps;
                  return (
                    <div className="top-header-secondmemories-search__field" ref={InputProps.ref}>
                      <SearchIcon className="top-header-secondmemories-search__icon"/>
                      <input
                        {...restInputProps}
                        className={`top-header-secondmemories-search__input ${inputClassName}`}
                        placeholder="Search Second Memories"
                      />
                    </div>
                  );
                }}
              />
            </>
          )}
        </div>

        {/* Right — primary actions */}
        <div className="top-header__right">
          {embed && <OpenStandaloneButton />}
          {!embed && (
            <>
              <FancyIconButton
                variant="primary"
                shape="circle"
                ariaLabel="Messages"
                className="header-messages-button"
                style={{ width: 38, height: 38 }}
                onClick={() => alert('Messages not implemented yet.')}
              >
                <ChatBubbleOutlineIcon/>
              </FancyIconButton>
              <AccountButton />
            </>
          )}
        </div>
      </div>
      {!embed && sideNavOpen && (
        <>
          <aside className="top-header-side-nav" aria-label="Second Memories navigation">
            <div className="top-header-side-nav__top">
              <div className="top-header-side-nav__profile">
                <MyAvatar userAccount={viewingUserAccount} name={viewingUserName} style={{width:56, height:56}}/>
                <div className="top-header-side-nav__profile-text">
                  <div className="top-header-side-nav__profile-name">{viewingUserName || 'Second Memories'}</div>
                  <div className="top-header-side-nav__profile-subtitle">Viewing map</div>
                </div>
              </div>
              <button
                className="top-header-side-nav__close"
                type="button"
                aria-label="Close navigation"
                onClick={() => dispatch(setSideNavOpen(false))}
              >
                <CloseIcon/>
              </button>
            </div>
            <nav className="top-header-side-nav__items">
              {navItems.map(item => (
                <button
                  className={`top-header-side-nav__item${item.isSelected ? ' is-selected' : ''}`}
                  type="button"
                  key={item.label}
                  aria-current={item.isSelected ? 'page' : undefined}
                  onClick={() => handleNavItemClick(item)}
                >
                  <span className="top-header-side-nav__item-icon">{item.icon}</span>
                  <span>{item.label}</span>
                </button>
              ))}
            </nav>
          </aside>
        </>
      )}
    </>
  );
}
